import type * as THREE from 'three/webgpu';
import type { ShadowFlags } from './lightModel';
import { DYNAMIC_CASTER_USERDATA } from './worldLights';

/**
 * ── ⭐ B3 · THE ONE APPLIER OF `castShadow` / `receiveShadow` ─────────────────────────────────
 *
 * `lightModel.SHADOW_FLAGS` and `EntityMeta.shadow` DECLARE what a class does with shadows; this
 * is the only place the declaration is written onto meshes. Everything else in `src/` that used to
 * write the two flags by hand (nineteen sites, nine files) now goes through here, and
 * `tests/renderShadowReceivers.test.ts` scans for a new bare write — because the owner's "the
 * character doesn't receive shadows" was ONE missing line that nothing could have caught.
 *
 * ── WHY A WALK PLUS A HOOK, NOT A WALK ──────────────────────────────────────────────────────────
 *
 * The objects a declaration covers are not all there when it is applied. The player group exists
 * at scene construction; the real character glTF lands under `rollSpin` seconds later
 * (`CharacterView.load`). The companion's hull is drawn in `create()`; his skinned rig lands under
 * `rigMount` when its fetch completes. A `static_mesh`'s group is empty until 50 MB of glTF
 * parses. A one-shot walk would leave every one of those at three's defaults (`receiveShadow =
 * false`) — the exact bug being fixed, re-created for every late arrival. So the walk stamps the
 * flags on every object it visits and hooks `childadded` on each, exactly the shape of
 * `scene.ts`'s `optInSubtree` (which exists for the same reason, one property over).
 *
 * The flags are STORED (`userData[SHADOW_FLAGS_USERDATA]`) and read back at hook time rather than
 * closed over, so re-applying to a root with different flags — the host applying a class column
 * over the entity root's default — overwrites what a later arrival will get. One hook per object,
 * guarded by a marker; a second `applyShadowFlags` on the same subtree installs nothing twice.
 *
 * ── WHAT IT WRITES, AND WHY ONCE ────────────────────────────────────────────────────────────────
 *
 * `object.receiveShadow` is in the per-OBJECT pipeline cache key (`RenderObject.js:843`
 * `cacheKey += object.receiveShadow`), so flipping it rebuilds that object's render object. It is
 * therefore written at construction and on arrival, never per frame — and never by a per-frame
 * caller. `castShadow` on a mesh is read by the shadow pass's `_projectObject`; it is not in a
 * material key. `movable` sets or CLEARS `worldLights.DYNAMIC_CASTER_USERDATA` on the root — clears,
 * because the entity root's default stamps every arrival movable and the class column must be
 * able to take that back for a static prop (Sponza's walls would otherwise be missing from every
 * frozen shadow map).
 */
export const SHADOW_FLAGS_USERDATA = 'fwgShadowFlags';
const HOOK_USERDATA = 'fwgShadowHook';

type Hooked = THREE.Object3D & { userData: Record<string, unknown> };

/**
 * Apply `flags` to every mesh under `root` (inclusive), stamp the subtree so later arrivals get the
 * same, and mark/unmark the root as a dynamic caster. Returns the number of meshes written — the
 * observable a test reads, and what `shadowCensus` counts back off the graph.
 */
/**
 * ── ⭐ `markRoot: false` — THE MARK DESCRIBES A CASTER, NEVER A CONTAINER ────────────────────────
 *
 * `DYNAMIC_CASTER_USERDATA` means *"skip this SUBTREE whole"*: `enableStaticShadowLayer` returns on
 * it, so nothing beneath a marked object opts into `FWG_STATIC_SHADOW_LAYER`. That is exactly right
 * for the player, whose character mesh arrives under him seconds late and must never be burned into
 * a frozen map — and exactly wrong for `entityRoot`, which is not a caster at all but the container
 * every static wall in the level hangs from.
 *
 * ⚠️ MEASURED CONSEQUENCE, LATENT ONLY BECAUSE NO WORLD AUTHORS `staticOnly` YET. `scene.ts` stamps
 * `entityRoot` with `ENTITY_SHADOW_DEFAULT` (`movable: true` — conservative on purpose, because an
 * object parented from a later callback cannot be attributed to a class and a mover baked into a
 * frozen map is a permanent ghost, strictly worse than a missing shadow). The mark landed on the
 * container, so `enableStaticShadowLayer` pruned the ENTIRE world: `__fwg.state().shadows.receivers
 * .entities` reads `{meshes: 1319, casting: 1317, dynamic: true}` — and a `staticOnly` light would
 * have frozen an EMPTY map. `FWG_STATIC_SHADOW_LAYER`'s own note calls that "worse than useless".
 *
 * ⛔ THE FIX IS NOT TO FLIP THE DEFAULT TO `movable: false`. The conservatism is the point; what
 * was wrong is applying a CASTER's property to a CONTAINER. So the subtree stamp and the
 * `childadded` hook still happen — every later arrival still takes the conservative answer — and
 * only the root's own mark is withheld. Every other caller passes a real caster and wants the mark,
 * so their contract is untouched and the default is `true`.
 */
export function applyShadowFlags(
  root: THREE.Object3D,
  flags: ShadowFlags,
  opts: { markRoot?: boolean } = {},
): number {
  let meshes = 0;
  root.traverse((o) => {
    const h = o as Hooked;
    h.userData[SHADOW_FLAGS_USERDATA] = flags;
    if ((o as THREE.Mesh).isMesh === true) {
      // Sprites are not `isMesh` (their `SpriteNodeMaterial` is unlit) and are never touched.
      o.castShadow = flags.cast;
      o.receiveShadow = flags.receive;
      meshes++;
    }
    if (h.userData[HOOK_USERDATA] !== true) {
      h.userData[HOOK_USERDATA] = true;
      o.addEventListener('childadded', (e) => {
        const current = h.userData[SHADOW_FLAGS_USERDATA] as ShadowFlags | undefined;
        if (current) applyShadowFlags(e.child, current);
      });
    }
  });
  // `markRoot: false` withholds the root's own mark WITHOUT weakening the subtree stamp or the
  // hook above — see this function's header for the container-versus-caster argument.
  if (flags.movable && opts.markRoot !== false) root.userData[DYNAMIC_CASTER_USERDATA] = true;
  else delete root.userData[DYNAMIC_CASTER_USERDATA];
  return meshes;
}

/** What a subtree's meshes actually carry — read off the graph, never off a declaration. */
export interface ShadowCensus {
  meshes: number;
  casting: number;
  receiving: number;
  /** The root carries the dynamic-caster mark (excluded from frozen maps as a whole). */
  dynamic: boolean;
}

export function shadowCensus(root: THREE.Object3D | null): ShadowCensus {
  const out: ShadowCensus = { meshes: 0, casting: 0, receiving: 0, dynamic: false };
  if (!root) return out;
  out.dynamic = root.userData[DYNAMIC_CASTER_USERDATA] === true;
  root.traverse((o) => {
    if ((o as THREE.Mesh).isMesh !== true) return;
    out.meshes++;
    if (o.castShadow) out.casting++;
    if (o.receiveShadow) out.receiving++;
  });
  return out;
}
